<template>
  <div>
    <PageHeaderBanner>
      <template #title>為替レート設定</template>
    </PageHeaderBanner>
    <q-form autofocus greedy novalidate @submit="isConfirmationFlg = true">
      <div class="cmi00310-form-area cmi00310-form">
        <div class="cmi00310-input-area q-gutter-y-sm">
          <div class="flex justify-center">
            <div class="left-input-area">
              <label class="requier">USD / JPY</label>
              <q-input
                v-model="inputData.dollar_exchange_rate"
                :rules="[
                  (val) => isEmpty(val) || $t('validation.required'),
                  (val) => isDecimalDigits(val) || $t('validation.numeric_only'),
                  (val) => isTwoDecimalPlaces(val) || $t('validation.two_decimal_places'),
                  (val: number) => val < 1000 || $t('validation.max_integer', { param1: '3' }),
                ]"
                outlined
                maxlength="6"
                dense
                suffix="円"
                input-class="text-right"
                class="input-area exception-right"
                lazy-rules
                @keydown.enter.prevent
              />
            </div>
            <div>
              <label>最終更新日時</label>
              <p class="q-mb-none q-ml-sm q-mt-sm">{{ inputData.dollar_update_timestamp }}</p>
            </div>
          </div>
          <div class="flex justify-center">
            <div class="left-input-area">
              <label class="requier">EUR / JPY</label>
              <q-input
                v-model="inputData.euro_exchange_rate"
                :rules="[
                  (val) => isEmpty(val) || $t('validation.required'),
                  (val) => isDecimalDigits(val) || $t('validation.numeric_only'),
                  (val) => isTwoDecimalPlaces(val) || $t('validation.two_decimal_places'),
                  (val: number) => val < 1000 || $t('validation.max_integer', { param1: '3' }),
                ]"
                outlined
                maxlength="6"
                dense
                suffix="円"
                input-class="text-right"
                class="input-area exception-right"
                lazy-rules
                @keydown.enter.prevent
              />
            </div>
            <div>
              <label>最終更新日時</label>
              <p class="q-mb-none q-ml-sm q-mt-sm">{{ inputData.euro_update_timestamp }}</p>
            </div>
          </div>
          <div class="flex justify-center">
            <div class="left-input-area">
              <label class="requier">GBP / JPY</label>
              <q-input
                v-model="inputData.pound_exchange_rate"
                :rules="[
                  (val) => isEmpty(val) || $t('validation.required'),
                  (val) => isDecimalDigits(val) || $t('validation.numeric_only'),
                  (val) => isTwoDecimalPlaces(val) || $t('validation.two_decimal_places'),
                  (val: number) => val < 1000 || $t('validation.max_integer', { param1: '3' }),
                ]"
                outlined
                maxlength="6"
                dense
                suffix="円"
                input-class="text-right"
                class="input-area exception-right"
                lazy-rules
                @keydown.enter.prevent
              />
            </div>
            <div>
              <label>最終更新日時</label>
              <p class="q-mb-none q-ml-sm q-mt-sm">{{ inputData.pound_update_timestamp }}</p>
            </div>
          </div>
          <FormFooterBanner style="position: inherit">
            <template #button-area>
              <div class="row justify-end">
                <q-btn
                  color="primary"
                  style="width: 130px"
                  class="q-mr-md"
                  label="変更"
                  type="submit"
                />
                <q-btn
                  outline
                  type="reset"
                  style="
                    width: 130px;
                    background: var(--q-white) !important;
                    color: var(--q-delete);
                  "
                  label="リセット"
                  icon="cached"
                  @click="initRate"
                />
              </div>
            </template>
          </FormFooterBanner>
          <!--更新成功ダイアログ-->
          <AlertDialog
            :display="isConfirmationFlg"
            level="information"
            :title="'更新確認'"
            :text="['入力された内容で更新します。', 'よろしいですか？']"
            @confirm="updateRate()"
            @close="isConfirmationFlg = false"
          />
          <!--更新成功ダイアログ-->
          <AlertDialog
            :display="isUpdatedDisplayFlg"
            level="information"
            confirmation
            :title="'更新完了'"
            :text="['更新が完了しました。']"
            @complete="isUpdatedDisplayFlg = false"
          />
          <!-- ダイアログ -->
          <AlertDialog
            :display="uploadErrorFLg"
            level="error"
            :title="dialogTitle"
            :text="dialogText"
            @complete="uploadErrorFLg = false"
          />
        </div>
      </div>
    </q-form>
  </div>
</template>

<script setup lang="ts">
import axios from 'axios';
import { Loading } from 'quasar';
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';
import { isEmpty, isTwoDecimalPlaces, isDecimalDigits } from '../../common/utils/validateUtils';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import FormFooterBanner from '../../components/banner/FormFooterBanner.vue';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';

const router = useRouter();
// Dialogタイトル
const dialogTitle = ref('');
// Dialog本文
const dialogText = ref(['']);
// 更新確認ダイアログ表示フラグ
const isConfirmationFlg = ref(false);
// 更新完了ダイアログ表示フラグ
const isUpdatedDisplayFlg = ref(false);
// 更新成功フラグ
const isUpdatedFlg = ref(false);
// エラーDialog表示フラグ
const uploadErrorFLg = ref(false);
// 初期表示データ
const initData = reactive({
  dollar_exchange_rate: '',
  euro_exchange_rate: '',
  pound_exchange_rate: '',
  dollar_update_timestamp: '',
  euro_update_timestamp: '',
  pound_update_timestamp: '',
  currency_code: '',
});

// 入力データ
const inputData = ref({ ...initData });

/**
 * 為替レートを初期化
 */
const initRate = () => {
  inputData.value = { ...initData };
};

/**
 * 為替レート取得
 */
const showRate = async () => {
  // Loading Start
  Loading.show();

  // 為替レート取得
  await axios
    .post('/cmd011001/showRate')
    .then((res) => {
      const { common, getCurrencyRateList } = res.data;

      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          getCurrencyRateList.forEach(
            (value: { currency_code: string; exchange_rate: string; update_timestamp: string }) => {
              if (value.currency_code === 'EUR') {
                initData.euro_exchange_rate = value.exchange_rate;
                initData.euro_update_timestamp = value.update_timestamp;
              } else if (value.currency_code === 'USD') {
                initData.dollar_exchange_rate = value.exchange_rate;
                initData.dollar_update_timestamp = value.update_timestamp;
              } else if (value.currency_code === 'GBP') {
                initData.pound_exchange_rate = value.exchange_rate;
                initData.pound_update_timestamp = value.update_timestamp;
              }
            }
          );
          inputData.value = { ...initData };
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          if (res.data.common.detail_status === DetailResultStatusCode.PARAMETER_ERROR) {
            dialogTitle.value = '検索条件入力エラー';
            dialogText.value = [res.data.message];
            uploadErrorFLg.value = true;
          } else {
            router.push({ name: 'error' });
          }

          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.res.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

/**
 *  為替レート情報の更新
 */
const updateRate = async () => {
  // 係数の小数部を2桁に固定する
  inputData.value.dollar_exchange_rate = Number(inputData.value.dollar_exchange_rate).toFixed(2);
  inputData.value.euro_exchange_rate = Number(inputData.value.euro_exchange_rate).toFixed(2);
  inputData.value.pound_exchange_rate = Number(inputData.value.pound_exchange_rate).toFixed(2);

  isConfirmationFlg.value = false;
  isUpdatedDisplayFlg.value = false;
  // Loading Start
  Loading.show();
  // 変更用データ作成
  const formData = new FormData();
  formData.append('dollar_exchange_rate', inputData.value.dollar_exchange_rate);
  formData.append('euro_exchange_rate', inputData.value.euro_exchange_rate);
  formData.append('pound_exchange_rate', inputData.value.pound_exchange_rate);
  formData.append('old_dollar_exchange_rate', initData.dollar_exchange_rate);
  formData.append('old_euro_exchange_rate', initData.euro_exchange_rate);
  formData.append('old_pound_exchange_rate', initData.pound_exchange_rate);
  await axios
    .post('/cmd011001/editRate', formData)
    .then((res) => {
      const { common, getCurrencyRateList } = res.data;

      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // 削除確認ダイアログのtitleを設定
          dialogTitle.value = '更新完了';
          // 削除確認ダイアログのtextを生成
          dialogText.value = ['為替レートが更新されました。'];
          isUpdatedDisplayFlg.value = true;
          isUpdatedFlg.value = true;
          getCurrencyRateList.forEach(
            (value: { currency_code: string; exchange_rate: string; update_timestamp: string }) => {
              if (value.currency_code === 'EUR') {
                initData.euro_exchange_rate = value.exchange_rate;
                initData.euro_update_timestamp = value.update_timestamp;
              } else if (value.currency_code === 'USD') {
                initData.dollar_exchange_rate = value.exchange_rate;
                initData.dollar_update_timestamp = value.update_timestamp;
              } else if (value.currency_code === 'GBP') {
                initData.pound_exchange_rate = value.exchange_rate;
                initData.pound_update_timestamp = value.update_timestamp;
              }
            }
          );
          inputData.value = { ...initData };
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          if (res.data.common.detail_status === DetailResultStatusCode.PARAMETER_ERROR) {
            dialogTitle.value = '設定値入力エラー';
            dialogText.value = [res.data.common.message];
            uploadErrorFLg.value = true;
          } else {
            router.push({ name: 'error' });
          }

          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他エラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};
// 初期表示
showRate();
</script>
<style scoped>
.cmi00310-form-area {
  width: 80%;
  max-width: 1020px;
  margin: 0 auto;
}
.cmi00310-input-area {
  width: 80%;
  margin: 30px auto 0;
  padding-right: 57px;
}

.approve-setting,
.mailaddress,
.code {
  margin-left: 0;
}

.left-input-area {
  margin-right: 140px;
  width: 230px;
}
</style>

<style>
.cmi00310-form-area.cmi00310-form .input-area.q-field.exception-right .q-field__native {
  padding-right: 34px;
}

.cmi00310-form-area.cmi00310-form
  .input-area.q-field.exception-right.q-field--error
  .q-field__native {
  padding-right: 60px;
}

.cmi00310-form-area.cmi00310-form .input-area.q-field.exception-right .q-field__suffix {
  position: absolute;
  right: 12px;
}

.cmi00310-form-area.cmi00310-form
  .input-area.q-field.exception-right.q-field--error
  .q-field__suffix {
  position: absolute;
  right: 40px;
}
</style>
